import { jsxs, jsx } from 'react/jsx-runtime';
import { TransformWrapper, TransformComponent, useControls } from 'react-zoom-pan-pinch';
import React__default, { useRef, useState } from 'react';
import { styled, css } from 'styled-components';
import { cssVar } from '../../theme/variables.js';
import { mediaTablet, mediaDesktop } from '../../constants/styled-utils.js';
import { renderCssBasedOnColorScheme } from '../../theme/renderCssBasedOnColorScheme.js';
import { useImageDimension } from '@fabric-app/hooks/useImageDimension';
import { useBoolState } from '@fabric-app/hooks/useBooleanState';
import { useDomContentRect } from '@fabric-app/hooks/useDomContentRect';
import { AnimatePresence, motion } from 'framer-motion';
import Skeleton from '../Skeleton.js';
import { framerAnimationFade } from '../../constants/framerAnimations.js';
import { Spinner } from '../Spinner.js';
import { useRunOnce } from '@fabric-app/hooks/useRunOnce';

const PinchZoomImageContainer = styled.div.withConfig({
  displayName: "PinchZoomImage__PinchZoomImageContainer",
  componentId: "sc-8e8cbdb9-0"
})([
  `min-height:100px;max-height:100%;height:100%;width:100%;max-width:100%;will-change:height;flex-grow:1;`,
  ` .pinch-zoom-image-wrapper{max-height:100%;height:100%;width:100%;}.pinch-zoom-image-content{max-height:100%;height:100%;position:relative;max-width:100%;`,
  `{max-height:100%;width:max-content;max-width:100%;display:flex;flex-direction:column;justify-content:center;align-items:center;}img{pointer-events:initial;max-height:100%;}}`,
  `{padding:0.5rem;.pinch-zoom-image-wrapper{overflow:hidden;border-radius:0.5rem 0.5rem 0.75rem 0.75rem;aspect-ratio:var(--image-aspectRatio);max-height:100%;}}`
], renderCssBasedOnColorScheme({
  light: css([
    `background:`,
    `;[data-fullscreen='true'] &{background:`,
    `;}`
  ], cssVar["color-bg-tertiary"], cssVar["color-bg-primary"]),
  dark: css([
    `background:`,
    `;`
  ], cssVar["color-bg-primary"])
}), mediaTablet, mediaDesktop);
const Image = styled.img.withConfig({
  displayName: "PinchZoomImage__Image",
  componentId: "sc-8e8cbdb9-1"
})([
  `max-width:100%;object-fit:contain;opacity:0;transition:0.2s opacity;will-change:height;position:absolute;top:50% !important;left:50% !important;transform:translate(-50%,-50%) !important;`,
  ` `,
  `{border-radius:0.5rem;}`,
  `{height:auto;`,
  `}`
], (p) => p.$isLoaded && css([
  `opacity:1;`
]), mediaDesktop, mediaTablet, (p) => p.$isVertical && css([
  `height:`,
  `px;max-height:100%;width:auto;max-width:100%;`
], p.$cssHeight));
const MIN_SCALE = 1;
const INIT_SCALE = Math.max(1, MIN_SCALE);
const MAX_ZOOM = 8;
const PinchZoomImageComponent = /* @__PURE__ */ React__default.memo(/* @__PURE__ */ React__default.forwardRef(function PinchZoomImg(props, ref) {
  const { cssHeight, maxWidth, onImageLoad, centerViewAfterLoad, ...imageProperties } = props;
  const imageSize = useImageDimension(props.src);
  const isVertical = imageSize.height > imageSize.width;
  const { handleTrue: setAsLoaded, value: isLoaded } = useBoolState();
  const { centerView } = useControls();
  React__default.useEffect(() => {
    if (props.disableGestures) {
      centerView(1, 0);
    }
  }, [
    props.disableGestures,
    centerView
  ]);
  const onLoad = React__default.useCallback((e) => {
    if (!isLoaded) {
      onImageLoad?.(e);
      if (centerViewAfterLoad) {
        setTimeout(() => {
          centerView(1, 0);
        }, 100);
      }
      setAsLoaded();
    }
  }, [
    centerView,
    setAsLoaded,
    onImageLoad,
    isLoaded,
    centerViewAfterLoad
  ]);
  React__default.useEffect(() => {
    centerView(void 0, 0);
  }, [
    cssHeight,
    maxWidth,
    centerView
  ]);
  return /* @__PURE__ */ jsx(Image, {
    ref,
    ...imageProperties,
    $isLoaded: isLoaded,
    draggable: false,
    onLoad,
    $isVertical: isVertical,
    $cssHeight: cssHeight
  });
}));
const PinchZoomImage = /* @__PURE__ */ React__default.forwardRef(function PinchZoomImage2({ disableGestures, srcHq, ...props }, ref) {
  const containerRef = useRef(null);
  const [domRect] = useDomContentRect(containerRef);
  const [isLoaded, setIsLoaded] = React__default.useState(false);
  const [aspectRatio, setAspectRatio] = React__default.useState(null);
  const handleCenterViewOnScale1 = (transform) => {
    if (transform.state.scale <= 1) {
      transform.centerView(1, 100);
    }
  };
  const [scale, setScale] = React__default.useState(1);
  const [displayHqImage, setDisplayHqImage] = useState(false);
  useRunOnce(() => {
    setDisplayHqImage(true);
  }, {
    condition: scale > 2
  });
  return /* @__PURE__ */ jsxs(PinchZoomImageContainer, {
    "data-testid": "item-content-section",
    ref: containerRef,
    "data-pan-id-ignore-expanded-resource": scale > 1 ? "xy" : false,
    style: aspectRatio ? {
      aspectRatio: `${aspectRatio.width} / ${aspectRatio.height}`
    } : void 0,
    children: [
      /* @__PURE__ */ jsx(AnimatePresence, {
        children: !isLoaded && /* @__PURE__ */ jsx(Skeleton, {
          ...framerAnimationFade,
          as: motion.div,
          style: {
            width: "100%",
            height: "100%",
            position: "absolute",
            inset: 0,
            zIndex: 100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column"
          },
          children: /* @__PURE__ */ jsx(Spinner, {
            size: 24,
            color: cssVar["color-text-placeholder"]
          })
        })
      }),
      /* @__PURE__ */ jsx(TransformWrapper, {
        initialScale: INIT_SCALE,
        minScale: MIN_SCALE,
        maxScale: MAX_ZOOM,
        centerOnInit: true,
        centerZoomedOut: true,
        onPanningStart: (ref2, event) => {
          if (ref2.state.scale <= 1) {
            event.preventDefault();
          }
        },
        onZoom: (ref2) => {
          setScale(Math.max(ref2.state.scale, MIN_SCALE));
        },
        panning: {
          lockAxisX: scale === 1,
          lockAxisY: scale === 1
        },
        onPanningStop: handleCenterViewOnScale1,
        onZoomStop: handleCenterViewOnScale1,
        pinch: {
          step: 0.2
        },
        wheel: {
          smoothStep: 0.01
        },
        disabled: disableGestures,
        limitToBounds: true,
        children: /* @__PURE__ */ jsx(TransformComponent, {
          contentClass: "pinch-zoom-image-content",
          wrapperClass: "pinch-zoom-image-wrapper",
          children: /* @__PURE__ */ jsxs("div", {
            style: {
              height: "100%",
              width: "100%",
              position: "relative",
              aspectRatio: `var(--image-aspectRatio)`
            },
            children: [
              /* @__PURE__ */ jsx(PinchZoomImageComponent, {
                onImageLoad: (e) => {
                  setIsLoaded(true);
                  setAspectRatio({
                    width: e.currentTarget.naturalWidth,
                    height: e.currentTarget.naturalHeight
                  });
                },
                cssHeight: domRect?.height ? domRect.height : 0,
                maxWidth: domRect?.width ?? 0,
                ref,
                centerViewAfterLoad: true,
                ...props
              }),
              displayHqImage && srcHq && /* @__PURE__ */ jsx(PinchZoomImageComponent, {
                onImageLoad: () => {
                  setIsLoaded(true);
                },
                cssHeight: domRect?.height ? domRect.height : 0,
                maxWidth: domRect?.width ?? 0,
                ref,
                ...props,
                style: {
                  zIndex: 20
                },
                src: srcHq
              })
            ]
          })
        })
      }, props.src)
    ]
  });
});

export { PinchZoomImage };
//# sourceMappingURL=PinchZoomImage.js.map
